import React from "react";
import { Eye } from "lucide-react";
import { Button } from "@cms/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@cms/ui/components/dialog";

const DISPLAY_TOKENS = [
  { key: "display-1", label: "Display 1" },
  { key: "display-2", label: "Display 2" },
  { key: "display-3", label: "Display 3" },
  { key: "display-4", label: "Display 4" },
];

const HEADING_TOKENS = [
  { key: "h1", label: "Heading 1" },
  { key: "h2", label: "Heading 2" },
  { key: "h3", label: "Heading 3" },
  { key: "h4", label: "Heading 4" },
  { key: "h5", label: "Heading 5" },
  { key: "h6", label: "Heading 6" },
];

const SAMPLE_PARAGRAPH =
  "The quick brown fox jumps over the lazy dog. Body copy uses the base font, size and " +
  "line height from your typography settings, so you can see how paragraphs will read on the published site.";

/**
 * Every display and heading size, plus a body paragraph, rendered in the
 * tenant's chosen fonts, scale and heading style (weight/style/casing/tracking).
 */
export function TypographyPreviewModal({ merged }: { merged: Record<string, string> }) {
  const v = (k: string) => merged[k];
  const headingStyle: React.CSSProperties = {
    fontFamily: v("font-heading"),
    fontWeight: v("heading-weight"),
    fontStyle: v("heading-style") as React.CSSProperties["fontStyle"],
    textTransform: v("heading-case") as React.CSSProperties["textTransform"],
    letterSpacing: v("heading-tracking"),
    lineHeight: v("leading-tight"),
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button type="button" size="sm" variant="outline">
          <Eye className="h-3.5 w-3.5" />
          Preview type
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Typography preview</DialogTitle>
          <DialogDescription>
            Every display and heading size, plus body copy, in the selected fonts, scale and heading style.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          {DISPLAY_TOKENS.map(({ key, label }) => (
            <div key={key} className="border-b pb-4">
              <p className="mb-1 text-xs text-muted-foreground">
                {label} — {v(key)}
              </p>
              <div style={{ ...headingStyle, fontSize: v(key) }}>Aa Bb Cc</div>
            </div>
          ))}
          {HEADING_TOKENS.map(({ key, label }) => (
            <div key={key} className="border-b pb-4">
              <p className="mb-1 text-xs text-muted-foreground">
                {label} — {v(key)}
              </p>
              <div style={{ ...headingStyle, fontSize: v(key) }}>The quick brown fox</div>
            </div>
          ))}
          <div>
            <p className="mb-1 text-xs text-muted-foreground">Paragraph — {v("font-size-base")}</p>
            <p
              style={{
                fontFamily: v("font-body"),
                fontSize: v("font-size-base"),
                lineHeight: v("leading-normal"),
              }}
            >
              {SAMPLE_PARAGRAPH}
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
